import { t } from "i18next";
import type { PublicBooking, PublicOption, PublicPrice } from "../types/PublicBooking";
import { formatPrice } from "../utils/formatPrice";
import textFormat from "../utils/textFormatter.util";
import { Flex } from "./Flex";
import Heading from "./Heading";
import ImageFullSizeButton from "./ImageFullSizeButton";
import Label from "./Label";
import Paragraph from "./Paragraph";
import RemoteImage from "./RemoteImage";
import { SelectionCard } from "./SelectionCard";

import "./BookingOptionPicker.css";

type BookingOptionPickerProps = {
    booking: PublicBooking | undefined;
    selectedOption: PublicOption | undefined;
    setSelectedOption: (option: PublicOption | undefined) => void;
};

export function BookingOptionPicker({
    booking,
    selectedOption,
    setSelectedOption,
}: BookingOptionPickerProps) {
    if (!booking?.options) {
        return null;
    }

    const { bookingStartTime, bookingEndTime } = textFormat.booking.time(
        booking.startTime,
        booking.startTimezone,
        booking.endTime,
        booking.endTimezone,
    );

    return (
        <Flex.Column gap="value28">
            <Flex.Row gap="value20" className="web-lib__booking-option-picker__header">
                <RemoteImage.Rounded.XXSmall
                    src={booking.imageURL}
                    alt={booking.title}
                />

                <Flex.Column gap="value4">
                    <Heading.H3 className="web-lib__booking-option-picker__title">
                        {booking.title}
                    </Heading.H3>

                    <Paragraph.Small.Reqular>
                        {bookingStartTime} - {bookingEndTime}
                    </Paragraph.Small.Reqular>
                </Flex.Column>
            </Flex.Row>

            <Label.Large.Bold transform="uppercase">
                {t("public_proposal__options-picker--title")}
            </Label.Large.Bold>

            <Flex.Column gap="value16">
                {booking.options?.map((option) => (
                    <BookingOption
                        key={option.id}
                        option={option}
                        selected={selectedOption?.id === option.id}
                        onSelect={() => {
                            if (selectedOption?.id === option.id) {
                                setSelectedOption(undefined);
                            } else {
                                setSelectedOption(option);
                            }
                        }}
                    />
                ))}
            </Flex.Column>
        </Flex.Column>
    );
}

type BookingOptionProps = {
    option: PublicOption;
    selected: boolean;
    onSelect: () => void;
};

export function BookingOption({ option, selected, onSelect }: BookingOptionProps) {
    return (
        <SelectionCard key={option.id} selected={selected} onSelect={onSelect}>
            <Flex.Row gap="value16" alignItems="start" justifyContent="space-between">
                <Flex.Column gap="value16">
                    <Heading.H3>{option.title}</Heading.H3>

                    <Paragraph.Small.Reqular>
                        {option.description}
                    </Paragraph.Small.Reqular>

                    {option.price && (
                        <Flex.Column gap="value4">
                            <Label.Small.Bold.Text1 transform="uppercase">
                                {t("public_booking__total-price")}
                            </Label.Small.Bold.Text1>

                            <Paragraph.Large.MediumBold.Text1>
                                <OptionPrice price={option.price} />
                            </Paragraph.Large.MediumBold.Text1>
                        </Flex.Column>
                    )}
                </Flex.Column>

                {option.imageURLs &&
                    option.imageURLs.length > 0 &&
                    option.imageURLs[0] && (
                        <div className="web-lib__share-booking__options__images">
                            <ImageFullSizeButton
                                url={option.imageURLs[0]}
                                alt={t("public_proposal__option")}
                            >
                                <RemoteImage.Square.XXSmall
                                    src={option.imageURLs[0]}
                                    alt={t(
                                        "booking_option_picker__option_image_alt_text",
                                        { optionTitle: option.title },
                                    )}
                                />
                            </ImageFullSizeButton>
                        </div>
                    )}
            </Flex.Row>
        </SelectionCard>
    );
}

function OptionPrice({ price }: { price: PublicPrice }) {
    const formatted = formatPrice(price);
    if (!formatted) {
        return null;
    }

    return (
        <>
            <span className="mr-4 font-sans">{formatted.currency}</span>
            {formatted.amount}
        </>
    );
}
